import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { all_routes } from "../../../router/all_routes";
import { useT } from "../../../../core/i18n";
import Api from "../../../../core/data/redux/utils/apiCall";
import {
  HallOption,
  STATUS_FILTERS,
  fetchHallBookings,
  fetchMasterOptions,
  getLoggedInEmpId,
  getRequestStatusUi,
  mapListRecord,
  parseDmy,
  resolveOptionLabel,
  statusMatches,
  syncHallBookings,
  todayYmd,
} from "./communityHall.service";

const PAGE_TITLE = "Booking of Community Hall/Common Room/Park";

const CommunityHallList: React.FC = () => {
  const { t } = useT();
  const navigate = useNavigate();
  const routes = all_routes;
  const label = (key: string, fallback: string) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  };

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [records, setRecords] = useState<ReturnType<typeof mapListRecord>[]>([]);
  const [requestTypes, setRequestTypes] = useState<HallOption[]>([]);
  const [staffQuarters, setStaffQuarters] = useState<HallOption[]>([]);

  const loadPage = useCallback(async (pageNumber: number, replace: boolean) => {
    const empId = getLoggedInEmpId();
    if (!empId) {
      toast.error(label("something_went_wrong", "Something went wrong"));
      return;
    }
    if (replace) setLoading(true);
    else setLoadingMore(true);
    try {
      const result = await fetchHallBookings(empId, pageNumber, 10);
      const mapped = result.items.map(mapListRecord);
      setRecords((prev) => (replace ? mapped : [...prev, ...mapped]));
      setHasMore(result.hasMore);
      setPage(pageNumber);
    } catch (err: any) {
      if (replace) setRecords([]);
      toast.error(Api.getErrorMessage(err) || err?.message || label("something_went_wrong", "Something went wrong"));
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMasterOptions()
      .then((options) => {
        setRequestTypes(options.requestTypes);
        setStaffQuarters(options.staffQuarters);
      })
      .catch(() => {});
    loadPage(1, true);
  }, [loadPage]);

  const filtered = useMemo(() => {
    return records.filter((item) => {
      if (!statusMatches(item.requestStatus, status)) return false;
      const created = parseDmy(item.requestCreationDate);
      if (startDate) {
        const [year, month, day] = startDate.split("-").map(Number);
        const from = new Date(year, month - 1, day);
        if (!created || created < from) return false;
      }
      if (endDate) {
        const [year, month, day] = endDate.split("-").map(Number);
        const to = new Date(year, month - 1, day, 23, 59, 59);
        if (!created || created > to) return false;
      }
      return true;
    });
  }, [records, status, startDate, endDate]);

  const handleSync = async () => {
    const empId = getLoggedInEmpId();
    if (!empId || syncing) return;
    setSyncing(true);
    try {
      const result = await syncHallBookings(empId);
      if (result.ok) {
        toast.success(result.message || label("sync_triggered_successfully", "Sync triggered successfully"));
        await loadPage(1, true);
      } else {
        toast.error(result.message || label("something_went_wrong", "Something went wrong"));
      }
    } catch (err: any) {
      toast.error(Api.getErrorMessage(err) || label("something_went_wrong", "Something went wrong"));
    } finally {
      setSyncing(false);
    }
  };

  const openDetails = (item: ReturnType<typeof mapListRecord>) => {
    const ref = encodeURIComponent(String(item.id || ""));
    navigate(routes.communityHall.view.replace(":referenceNumber", ref), { state: { request: item.raw } });
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">{label("booking_of_community_hall_common_room_park", PAGE_TITLE)}</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to={routes.services.list}>Services</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {label("booking_of_community_hall_common_room_park", PAGE_TITLE)}
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button type="button" className="btn btn-outline-secondary" onClick={handleSync} disabled={syncing || loading}>
              <i className={`ti ti-refresh me-1 ${syncing ? "ch-spin" : ""}`}></i>
              {syncing ? label("syncing", "Syncing") : t("sync")}
            </button>
            <button type="button" className="btn btn-outline-danger" onClick={() => navigate(routes.communityHall.overview)}>
              <i className="ti ti-list me-1"></i>
              {label("view_all_bookings", "View All Bookings")}
            </button>
            <button type="button" className="btn btn-danger" onClick={() => navigate(routes.communityHall.create)}>
              <i className="ti ti-plus me-1"></i>
              {t("create_new_request")}
            </button>
          </div>
        </div>

        <div className="card border-0 shadow-sm mb-3">
          <div className="card-body">
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label fw-semibold small">{t("start_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={startDate}
                  max={endDate || todayYmd()}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold small">{t("end_date")}</label>
                <input
                  type="date"
                  className="form-control"
                  value={endDate}
                  min={startDate || undefined}
                  max={todayYmd()}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold small">{label("select_status", "Select Status")}</label>
                <select className="form-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUS_FILTERS.map((item) => (
                    <option key={item} value={item}>
                      {item === "all"
                        ? label("all", "All")
                        : item === "Pending Draft"
                          ? label("pending_draft", "Pending Draft")
                          : item === "Approved"
                            ? label("approved", "Approved")
                            : item === "Rejected"
                              ? label("rejected", "Rejected")
                              : label("in_process", "In Process")}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-danger" role="status" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center py-5 text-muted">
              {label("no_requests_available_for_selected_filters", "No requests available for the selected filters")}
            </div>
          </div>
        ) : (
          <div className="row g-3">
            {filtered.map((item) => {
              const statusUi = getRequestStatusUi(item.requestStatus);
              return (
                <div className="col-xl-6" key={item.id}>
                  <div className="card border shadow-sm h-100">
                    <div className="card-header bg-white d-flex justify-content-between align-items-center gap-2">
                      <span className="fw-bold">
                        {label("reference_no", "Reference No")} : {item.id}
                      </span>
                      <span className={statusUi.badge}>{statusUi.label}</span>
                    </div>
                    <div className="card-body">
                      <InfoRow label={label("personal_no", "Personal No.")} value={item.personalNumber} />
                      <InfoRow
                        label={label("request_type", "Request Type")}
                        value={resolveOptionLabel(requestTypes, item.requestTypeCode)}
                      />
                      <InfoRow
                        label={label("staff_quarter", "Staff Quarters")}
                        value={resolveOptionLabel(staffQuarters, item.staffQuarterCode)}
                      />
                      <InfoRow
                        label={label("request_creation_date", "Request Creation Date")}
                        value={item.requestCreationDate}
                      />
                      <InfoRow label={label("approver_id", "Currently With")} value={item.approverId} />
                      <button type="button" className="btn btn-danger w-100 mt-3" onClick={() => openDetails(item)}>
                        {t("view_details")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {hasMore && !loading && (
          <div className="text-center mt-3">
            <button type="button" className="btn btn-outline-danger" disabled={loadingMore} onClick={() => loadPage(page + 1, false)}>
              {loadingMore ? label("loading", "Loading...") : label("load_more", "Load More")}
            </button>
          </div>
        )}
      </div>
      <style>{`
        .ch-spin { animation: ch-spin 1s linear infinite; }
        @keyframes ch-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const InfoRow: React.FC<{ label: string; value: any }> = ({ label, value }) => (
  <div className="d-flex justify-content-between gap-3 mb-2">
    <span className="text-muted fw-semibold">{label}</span>
    <span className="text-dark text-end">{value || "-"}</span>
  </div>
);

export default CommunityHallList;
